import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import * as yup from 'yup';

import { updateSubmissionData } from 'apis/legitimizer/kyb/api';
import { KybSubmissionData } from 'apis/legitimizer/types';

import { BeneficiariesFormFields } from './BeneficiariesFormFields';
import { SectionForm } from './SectionForm';
import { getBeneficiariesDetailsSchema, getPepsSchema } from './validation';
import { KybProcessContext } from '../KybProcessContext';
import { SectionConfigurationContext } from '../SectionConfigurationContext';
import {
  transformBeneficiariesFormValuesToApi,
  transformKybSubmissionToBeneficiariesFormValues,
} from '../transformers';
import { BeneficiariesFormValues } from '../types';
import { useBeneficiaryDetailsFieldsConfigMemo, usePEPDetailsFieldsConfigMemo } from '../useFieldsConfigMemo';

type Props = {
  feedback?: string;
  submissionData: KybSubmissionData;
  onSubmitSuccess: (section: string, skipSection?: boolean) => Promise<void>;
};

const Beneficiaries = ({ feedback, submissionData, onSubmitSuccess }: Props) => {
  const intl = useIntl();

  const [hasPeps, setHasPeps] = useState(false);

  const initialFormValues = useMemo(() => {
    setHasPeps((submissionData.beneficiaries?.pepDetails?.length ?? 0) > 0);
    return transformKybSubmissionToBeneficiariesFormValues(submissionData.beneficiaries);
  }, [submissionData]);

  const sectionConfiguration = useContext(SectionConfigurationContext);
  const { processId } = useContext(KybProcessContext);

  const beneficiaryDetailsFieldsConfig = useBeneficiaryDetailsFieldsConfigMemo({ sectionConfiguration });
  const pepDetailsFieldsConfig = usePEPDetailsFieldsConfigMemo({ sectionConfiguration });

  const beneficiariesFormSchema = useMemo(() => {
    let schema = yup.object({});
    schema = schema.concat(getBeneficiariesDetailsSchema(false, beneficiaryDetailsFieldsConfig));
    if (hasPeps) {
      schema = schema.concat(getPepsSchema(false, pepDetailsFieldsConfig));
    }
    return schema;
  }, [beneficiaryDetailsFieldsConfig, pepDetailsFieldsConfig, hasPeps]);

  const t = useCallback((id: string) => intl.formatMessage({ id }), [intl]);

  const handleSubmit = async (data: BeneficiariesFormValues) => {
    const { isRepresentativeSoleBusinessOwner, uboDetails, pepDetails } = transformBeneficiariesFormValuesToApi(
      data,
      submissionData,
    );
    const submitData = {
      ...submissionData,
      beneficiaries: {
        isRepresentativeSoleBusinessOwner,
        uboDetails,
        pepDetails: hasPeps ? pepDetails : [],
      },
    };
    await updateSubmissionData(processId, submitData);
    await onSubmitSuccess(data.nextSection ? data.nextSection : 'beneficiaries', !!data.nextSection);
  };

  return (
    <SectionForm
      subtitle={t('compliance.kyb.sections.beneficiaries.info')}
      initialFormValues={initialFormValues}
      schema={beneficiariesFormSchema}
      feedback={feedback}
      onSubmit={handleSubmit}
    >
      {({ submitting }) => (
        <BeneficiariesFormFields
          hasPeps={hasPeps}
          setHasPeps={setHasPeps}
          disabled={submitting}
          submissionData={submissionData}
        />
      )}
    </SectionForm>
  );
};

export default Beneficiaries;
